/**
 * EasyInstallerStep Component
 *
 * Step 4: Reconnect to device and load Easy Installer
 * After the bootloader transfer, the device reboots with a new VID:PID.
 * This step handles reconnection and loading of hawkin_recovery.itb and boot-sdp.scr.
 * Once loaded, the device boots into Easy Installer with the flashing green light.
 */

import { Button } from '@ui/shared/Button';
import * as React from 'react';
import styled from 'styled-components';
import { Icon } from '../../../../shared/Icon';
import { useUpdaterServices } from '../../hooks/useUpdaterServices';
import { useWizard } from '../../hooks/useWizard';
import { IMX_DEVICES, wrapHIDDevice } from '../../types/imx-types';
import {
  DeviceInfo,
  ErrorCode,
  RecoveryAction,
  StateActionType,
  StepState,
  UpdaterError,
  WizardStep,
} from '../../types/wizard-types';
import { HelpFooter } from '../HelpFooter';
import {
  PhaseContent,
  PhaseHint,
  PhaseIcon,
  PhaseItem,
  PhaseLabel,
  PhaseList,
  StepContainer,
} from '../StepStyles';
import { TransferProgress } from '../TransferProgress';
import { WarningBanner } from '../WarningBanner';

/**
 * Transfer phases for Easy Installer loading
 */
type TransferPhase =
  | 'waiting_reconnect'
  | 'reconnecting'
  | 'connecting'
  | 'loading_tezi'
  | 'loading_script'
  | 'booting'
  | 'complete';

/**
 * EasyInstallerStep component
 */
export const EasyInstallerStep: React.FC = (): React.ReactElement => {
  const { state, setProgress, dispatch, setError, nextStep, setDeviceInfo } =
    useWizard();
  const { setHidDevice, imxLoader, logger, loadFile, setProgressCallback } =
    useUpdaterServices();

  const [phase, setPhase] = React.useState<TransferPhase>('waiting_reconnect');
  const [hasStarted, setHasStarted] = React.useState(false);
  const [showReconnectPrompt, setShowReconnectPrompt] = React.useState(true);

  /**
   * U-Boot mode device identifiers
   * After bootloader loads, device re-enumerates with these VID:PIDs
   */
  const UBOOT_DEVICE_FILTERS = React.useMemo((): Array<{
    vendorId: number;
    productId: number;
  }> => {
    const ubootDevices = IMX_DEVICES.filter(
      (d: { mode: string }): boolean => d.mode === 'U-Boot'
    );
    return ubootDevices.length > 0
      ? ubootDevices.map(
          (d: {
            vid: number;
            pid: number;
          }): { vendorId: number; productId: number } => ({
            vendorId: d.vid,
            productId: d.pid,
          })
        )
      : [{ vendorId: 0x1b67, productId: 0x4000 }]; // Default U-Boot VID:PID
  }, []);

  /**
   * Request device selection from user (manual reconnection)
   */
  const handleManualReconnect = async (): Promise<void> => {
    setPhase('reconnecting');
    setShowReconnectPrompt(false);
    setProgress(0, 'Waiting to start transfer...');

    try {
      // Log current filters for debugging
      logger.info('U-Boot plate filters:');
      UBOOT_DEVICE_FILTERS.forEach(
        (f: { vendorId: number; productId: number }): void => {
          logger.info(
            `  - VID: 0x${f.vendorId.toString(
              16
            )}, PID: 0x${f.productId.toString(16)}`
          );
        }
      );

      // First, log all currently known devices (ones we have permission for)
      try {
        const knownDevices = await navigator.hid.getDevices();
        logger.info(`Currently known HID plates (${knownDevices.length}):`);
        knownDevices.forEach((d: HIDDevice): void => {
          logger.info(
            `  - "${d.productName}" VID: 0x${d.vendorId.toString(
              16
            )}, PID: 0x${d.productId.toString(16)}, opened: ${d.opened}`
          );
        });
      } catch (e) {
        logger.debug('Could not enumerate known plates', e);
      }

      logger.info('Requesting reconnection from user...');

      // Request device with U-Boot filters to only show the force plate
      const devices = await navigator.hid.requestDevice({
        filters: UBOOT_DEVICE_FILTERS,
      });

      if (!devices || devices.length === 0) {
        setShowReconnectPrompt(true);
        setPhase('waiting_reconnect');
        logger.warning('No plate selected during reconnection');
        return;
      }

      const device = devices[0];
      logger.info(
        `User selected device: "${
          device.productName
        }" VID: 0x${device.vendorId.toString(
          16
        )}, PID: 0x${device.productId.toString(16)}`
      );

      await startEasyInstallerTransfer(device);
    } catch (err: unknown) {
      logger.error('Manual reconnection error', err);
      setShowReconnectPrompt(true);
      setPhase('waiting_reconnect');
      setProgress(0, 'Click "Reconnect Plates" to try again');
    }
  };

  /**
   * Perform the Easy Installer transfer
   */
  const startEasyInstallerTransfer = React.useCallback(
    async (device: HIDDevice): Promise<void> => {
      if (hasStarted) return;
      setHasStarted(true);

      // Mark step as in progress
      dispatch({
        type: StateActionType.SET_STEP_STATE,
        step: WizardStep.EASY_INSTALLER_LOADING,
        state: StepState.IN_PROGRESS,
      });

      try {
        // Phase 1: Opening device
        setPhase('connecting');

        // Open the device if not already open
        if (!device.opened) {
          await device.open();
        }

        // Store the device
        setHidDevice(device);

        // Find matching device config
        const deviceConfig = IMX_DEVICES.find(
          (d: { vid: number; pid: number }): boolean =>
            d.vid === device.vendorId && d.pid === device.productId
        );

        // Update device info
        const deviceInfo: DeviceInfo = {
          vendorId: device.vendorId,
          productId: device.productId,
          productName:
            device.productName || deviceConfig?.name || 'U-Boot Plate',
          stage: 'uboot',
          config: deviceConfig || IMX_DEVICES[0],
        };
        setDeviceInfo(deviceInfo);

        logger.success(
          `Device reconnected: VID=0x${device.vendorId.toString(
            16
          )}, PID=0x${device.productId.toString(16)}`
        );

        // Set up progress callback - pass through direct 0-100% for this step
        setProgressCallback((percent: number, message: string): void => {
          setProgress(percent, message);
        });

        // Connect to device via IMX loader
        const wrappedDevice = wrapHIDDevice(device);
        const config = await imxLoader.connectDevice(wrappedDevice);
        logger.success(`Connected to U-Boot: ${config.name}`);

        // Phase 2: Loading hawkin_recovery.itb
        setPhase('loading_tezi');
        logger.info('Starting Easy Installer image transfer');

        // Set EID for injection into boot script
        if (state.eid) {
          imxLoader.setEid(state.eid);
          logger.info(`EID set for boot script injection: ${state.eid}`);
        }

        // Load images using the IMX loader service
        // The config.work items will specify hawkin_recovery.itb and boot-sdp.scr
        const success = await imxLoader.loadImages(loadFile);

        if (!success) {
          throw new Error('Update loading failed');
        }

        // Phase 3: Booting
        setPhase('booting');
        logger.info('Plates are booting into Easy Installer');

        // The device will now boot into Easy Installer
        // The power light should start flashing green
        await new Promise<void>((resolve: () => void): void => {
          setTimeout(resolve, 2000);
        });

        // Complete
        setPhase('complete');
        setProgress(100, 'Update loaded successfully!');
        logger.success(
          'Easy Installer loaded - plates should show flashing green power light'
        );

        // Mark step as completed
        dispatch({
          type: StateActionType.SET_STEP_STATE,
          step: WizardStep.EASY_INSTALLER_LOADING,
          state: StepState.COMPLETED,
        });

        // Clear progress callback
        setProgressCallback(undefined);

        // Auto-advance after a short delay
        await new Promise<void>((resolve: () => void): void => {
          setTimeout(resolve, 1500);
        });
        nextStep();
      } catch (err: unknown) {
        logger.error('Easy Installer transfer error', err);

        // Clear progress callback
        setProgressCallback(undefined);

        // If it's already an UpdaterError (e.g., from firmware service), use it directly
        if (err instanceof UpdaterError) {
          setError(err);
        } else {
          const message = err instanceof Error ? err.message : 'Unknown error';
          const stack = err instanceof Error ? err.stack : undefined;
          setError(
            new UpdaterError(
              `Update loading failed: ${message}`,
              ErrorCode.EASY_INSTALLER_TRANSFER_FAILED,
              WizardStep.EASY_INSTALLER_LOADING,
              [
                {
                  action: RecoveryAction.RETRY_STEP,
                  label: 'Retry',
                  description: 'Try loading the update again',
                  order: 1,
                },
                {
                  action: RecoveryAction.POWER_CYCLE,
                  label: 'Power Cycle Plates',
                  description:
                    'Disconnect power, wait 10 seconds, reconnect and start over',
                  order: 2,
                },
                {
                  action: RecoveryAction.START_OVER,
                  label: 'Start Over',
                  description: 'Restart the entire update process',
                  order: 3,
                },
              ],
              stack
            )
          );
        }
      }
    },
    [
      hasStarted,
      setHidDevice,
      setDeviceInfo,
      dispatch,
      setProgress,
      setError,
      nextStep,
      imxLoader,
      loadFile,
      setProgressCallback,
      logger,
    ]
  );

  /**
   * Get phase status
   */
  const getPhaseStatus = (
    targetPhase: TransferPhase
  ): 'pending' | 'active' | 'complete' => {
    const phases: TransferPhase[] = [
      'waiting_reconnect',
      'reconnecting',
      'connecting',
      'loading_tezi',
      'loading_script',
      'booting',
      'complete',
    ];
    const currentIndex = phases.indexOf(phase);
    const targetIndex = phases.indexOf(targetPhase);

    if (targetIndex < currentIndex) return 'complete';
    if (targetIndex === currentIndex) return 'active';
    return 'pending';
  };

  return (
    <StepContainer>
      {/* Warning Banner */}
      <WarningBanner
        title='Do not disconnect your plates!'
        message='Keep your plates connected until the update completes.'
      />

      {/* Reconnection Prompt */}
      {showReconnectPrompt && (
        <ReconnectSection>
          <ReconnectIcon>
            <Icon name='usb' size='medium' color='white' />
          </ReconnectIcon>
          <ReconnectContent>
            <ReconnectTitle>Reconnection Required</ReconnectTitle>
            <ReconnectDescription>
              Your Force Plates require a reconnection to load the new firmware.
              <br />
              <ol>
                <li>
                  Click the{' '}
                  <span style={{ fontWeight: 'bold' }}>Reconnect Plates</span>{' '}
                  button below to open the pop-up window
                </li>
                <li>
                  Select{' '}
                  <span style={{ fontWeight: 'bold' }}>
                    "USB download gadget"
                  </span>{' '}
                  from the list and click{' '}
                  <span style={{ fontWeight: 'bold' }}>Connect</span>.
                </li>
              </ol>
            </ReconnectDescription>
            <Button
              icon='usb'
              title='Reconnect Plates'
              flavor='primary'
              onClick={handleManualReconnect}
              disabled={phase !== 'waiting_reconnect'}
            />
          </ReconnectContent>
        </ReconnectSection>
      )}

      {/* Progress Display (shown after reconnection) */}
      {!showReconnectPrompt && (
        <>
          <TransferProgress
            progress={state.currentProgress}
            message={state.currentProgressMessage}
            isActive={phase !== 'complete'}
            isComplete={phase === 'complete'}
          />

          {/* Phase Breakdown */}
          <PhaseList>
            <PhaseItem $status={getPhaseStatus('connecting')}>
              <PhaseIcon $status={getPhaseStatus('connecting')}>
                {getPhaseStatus('connecting') === 'complete' ? (
                  <Icon name='check' color='white' size='tiny' />
                ) : (
                  <Icon name='link' size='tiny' />
                )}
              </PhaseIcon>
              <PhaseContent>
                <PhaseLabel>Connect to device</PhaseLabel>
                <PhaseHint>Establish connection</PhaseHint>
              </PhaseContent>
            </PhaseItem>

            <PhaseItem $status={getPhaseStatus('loading_tezi')}>
              <PhaseIcon $status={getPhaseStatus('loading_tezi')}>
                {getPhaseStatus('loading_tezi') === 'complete' ? (
                  <Icon name='check' color='white' size='tiny' />
                ) : (
                  <Icon name='cloud_download' size='tiny' />
                )}
              </PhaseIcon>
              <PhaseContent>
                <PhaseLabel>Load update files</PhaseLabel>
                <PhaseHint>Transfer update to plates</PhaseHint>
              </PhaseContent>
            </PhaseItem>

            <PhaseItem $status={getPhaseStatus('booting')}>
              <PhaseIcon $status={getPhaseStatus('booting')}>
                {getPhaseStatus('booting') === 'complete' ? (
                  <Icon name='check' color='white' size='tiny' />
                ) : (
                  <Icon name='play_arrow' size='tiny' />
                )}
              </PhaseIcon>
              <PhaseContent>
                <PhaseLabel>Start installer</PhaseLabel>
                <PhaseHint>Initialize firmware installer</PhaseHint>
              </PhaseContent>
            </PhaseItem>

            <PhaseItem $status={getPhaseStatus('complete')}>
              <PhaseIcon $status={getPhaseStatus('complete')}>
                {getPhaseStatus('complete') === 'complete' ? (
                  <Icon name='check' color='white' size='tiny' />
                ) : (
                  <Icon name='check_circle' size='tiny' />
                )}
              </PhaseIcon>
              <PhaseContent>
                <PhaseLabel>Complete</PhaseLabel>
                <PhaseHint>Ready to install firmware</PhaseHint>
              </PhaseContent>
            </PhaseItem>
          </PhaseList>
        </>
      )}

      {/* Contact Support */}
      <HelpFooter />
    </StepContainer>
  );
};

// Styled Components

const ReconnectSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 24px;
  background: var(--color-surface-1);
  border-radius: 10px;
  border: 2px dashed var(--color-primary);
`;

const ReconnectIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: var(--color-primary);
  border-radius: 50%;
  flex-shrink: 0;
`;

const ReconnectContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ReconnectTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
`;

const ReconnectDescription = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.72);
`;

const ReconnectButton = styled(Button)`
  align-self: flex-start;
  margin-top: 8px;

  span {
    margin-left: 8px;
  }
`;

export default EasyInstallerStep;
